/*
 * File: StudentTabs.tsx
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 * Copyright (c) DrivingDoc 2025
 */
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import type { CoursePeriod } from 'api/hooks/private/periods/GetCoursePeriod';

import { getDriveEnrollmentLabel } from 'pages/Admin/LegacyCalendar/util/getDriveEnrollmentLabel';

interface StudentTabsProps {
  enrollments: CoursePeriod['period_enrollments'];
  selectedStudentId: string;
  onTabChange: (event: React.SyntheticEvent, newValue: string) => void;
}

export default function StudentTabs({ enrollments, selectedStudentId, onTabChange }: StudentTabsProps) {
  if (enrollments.length === 0) {
    return null;
  }

  return (
    <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
      <Tabs
        value={selectedStudentId}
        onChange={onTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: '56px',
          '& .MuiTab-root': {
            minHeight: '56px',
            py: 2,
          },
        }}
      >
        {enrollments.map((enrollment) => {
          const enrollmentUser = enrollment.student.user;
          const enrollmentDriveLabel = getDriveEnrollmentLabel(enrollment, '#');
          const isSelected = selectedStudentId === enrollment.student_user_id;

          return (
            <Tab
              key={enrollment.student_user_id}
              value={enrollment.student_user_id}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" fontWeight={isSelected ? 'bold' : 'medium'}>
                    {enrollmentUser.first_name} {enrollmentUser.last_name} ({enrollmentDriveLabel})
                  </Typography>
                  {enrollment.attendance && (
                    <Chip
                      size="small"
                      label={
                        enrollment.attendance === 'present' ? 'P' : enrollment.attendance === 'absent' ? 'A' : 'NP'
                      }
                      color={enrollment.attendance === 'present' ? 'success' : 'error'}
                      sx={{ height: '20px', fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              }
            />
          );
        })}
      </Tabs>
    </Paper>
  );
}
